/**
 * Icon set — the snippet's Tabler-style stroke icons, extracted to path data
 * (from viewers/snippet/src/components/icons) and rendered by one <Icon>
 * component. Names match the snippet's icon registry so command `icon` ids
 * carry over verbatim. Stroke-based (currentColor); a few glyphs carry filled
 * sub-paths, marked `fill: true`.
 *
 * ACCENT SLOTS: a tool glyph tags the sub-paths that preview the tool's
 * drawing defaults — `stroke`/`fill: 'primary' | 'secondary'` paint from the
 * command's resolved `iconAccent` (slot placement ported 1:1 from the v2
 * hand-authored components). With no accent, a slotted stroke inherits
 * currentColor and a slotted fill renders nothing — pixel-identical to the
 * untagged glyph, so non-tool surfaces never change.
 *
 * GENERATED by scratchpad/extract-icons.mjs — 93 icons (slot tags by hand).
 */
import * as React from 'react';
import { useCustomIcons } from '../config-context';

export type PathSpec =
  | string
  | {
      d: string;
      /** Fill paint: `true` = currentColor (a filled glyph part); a slot
       *  name = that accent color (nothing when the accent is absent). */
      fill?: boolean | 'primary' | 'secondary';
      /** Stroke paint: a slot name = that accent color (currentColor when
       *  the accent is absent). */
      stroke?: 'primary' | 'secondary';
    };

/** A registrable icon: 24×24 stroke path data (Tabler/Lucide-style), exactly
 *  the shape of the built-in set — user icons are not a lesser kind. */
export type IconDef = readonly PathSpec[];

/** The two tintable slots — structurally identical to plugin-commands'
 *  IconAccent, kept local so the icon set stays dependency-free. */
export interface IconAccent {
  primary?: string;
  secondary?: string;
}

export const ICON_PATHS: Record<string, readonly PathSpec[]> = {
  alertTriangle: [
    'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
  ],
  alignBottom: [
    'M3 19v-14a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z',
    'M11 17h2',
    'M9 14h6',
    'M10 11h4',
  ],
  alignCenter: ['M4 6l16 0', 'M8 12l8 0', 'M6 18l12 0'],
  alignLeft: ['M4 6l16 0', 'M4 12l10 0', 'M4 18l14 0'],
  alignMiddle: [
    'M3 19v-14a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z',
    'M11 15h2',
    'M9 12h6',
    'M10 9h4',
  ],
  alignRight: ['M4 6l16 0', 'M10 12l10 0', 'M6 18l14 0'],
  alignTop: [
    'M3 19v-14a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z',
    'M11 13h2',
    'M9 10h6',
    'M10 7h4',
  ],
  arrowBackUp: ['M9 14l-4 -4l4 -4', 'M5 10h11a4 4 0 1 1 0 8h-1'],
  arrowForwardUp: ['M15 14l4 -4l-4 -4', 'M19 10h-11a4 4 0 1 0 0 8h1'],
  bold: ['M7 5h6a3.5 3.5 0 0 1 0 7h-6z', 'M13 12h1a3.5 3.5 0 0 1 0 7h-7v-7'],
  book: [
    'M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0',
    'M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0',
    'M3 6l0 13',
    'M12 6l0 13',
    'M21 6l0 13',
  ],
  book2: [
    'M19 4v16h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12z',
    'M19 16h-12a2 2 0 0 0 -2 2',
    'M9 8h6',
  ],
  callout: [
    {
      d: 'M20 6v4a1 1 0 0 1 -1 1h-14a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h14a1 1 0 0 1 1 1',
      stroke: 'primary',
      fill: 'secondary',
    },
    { d: 'M17 11v6h-9', stroke: 'primary' },
    { d: 'M8 15l-2 2l2 2', stroke: 'primary' },
  ],
  check: ['M5 12l5 5l10 -10'],
  chevronDown: ['M6 9l6 6l6 -6'],
  chevronLeft: ['M15 6l-6 6l6 6'],
  chevronRight: ['M9 6l6 6l-6 6'],
  circle: [
    { d: 'M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0', stroke: 'primary', fill: 'secondary' },
  ],
  comment: [
    'M12 11v.01',
    'M8 11v.01',
    'M16 11v.01',
    'M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3z',
  ],
  copy: [
    'M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z',
    'M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1',
  ],
  deviceFloppy: [
    'M6 4h10l4 4v10a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2',
    'M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M14 4l0 4l-6 0l0 -4',
  ],
  dots: [
    'M5 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
    'M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
    'M19 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
  ],
  doublePage: [
    'M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1zm9 -1v18',
  ],
  download: ['M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2', 'M7 11l5 5l5 -5', 'M12 4l0 12'],
  externalLink: [
    'M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6',
    'M11 13l9 -9',
    'M15 4h5v5',
  ],
  eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'],
  file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'],
  fileImport: [
    'M14 3v4a1 1 0 0 0 1 1h4',
    'M5 13v-8a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2h-5.5m-9.5 -2h7m-3 -3l3 3l-3 3',
  ],
  fitToPage: [
    'M12 20h-6a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h6',
    'M18 14v7',
    'M18 3v7',
    'M15 18l3 3l3 -3',
    'M15 6l3 -3l3 3',
  ],
  fitToWidth: [
    'M4 12v-6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v6',
    'M10 18h-7',
    'M21 18h-7',
    'M6 15l-3 3l3 3',
    'M18 15l3 3l-3 3',
  ],
  formCheckbox: [
    'M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14',
    'M9 12l2 2l4 -4',
  ],
  formListbox: [
    'M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14',
    'M11 8h6',
    'M11 12h6',
    'M11 16h6',
    'M7 8l0 .01',
    'M7 12l0 .01',
    'M7 16l0 .01',
  ],
  formRadio: [
    { d: 'M9 12a2 2 0 1 0 6 0a2 2 0 1 0 -6 0', fill: true },
    'M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0',
  ],
  formSelect: [
    'M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14',
    'M9 11l3 3l3 -3',
  ],
  formTextfield: [
    'M12 3a3 3 0 0 0 -3 3v12a3 3 0 0 0 3 3',
    'M6 3a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3',
    'M13 7h7a1 1 0 0 1 1 1v8a1 1 0 0 1 -1 1h-7',
    'M5 7h-1a1 1 0 0 0 -1 1v8a1 1 0 0 0 1 1h1',
    'M17 12h.01',
    'M13 12h.01',
  ],
  freeText: [
    { d: 'M4 7v-2h12v2', stroke: 'primary' },
    { d: 'M10 5v14', stroke: 'primary' },
    { d: 'M12 19h-4', stroke: 'primary' },
    { d: 'M15 13v-1h6v1', stroke: 'primary' },
    { d: 'M18 12v7', stroke: 'primary' },
    { d: 'M17 19h2', stroke: 'primary' },
  ],
  fullscreen: [
    'M4 8v-2a2 2 0 0 1 2 -2h2',
    'M4 16v2a2 2 0 0 0 2 2h2',
    'M16 4h2a2 2 0 0 1 2 2v2',
    'M16 20h2a2 2 0 0 0 2 -2v-2',
  ],
  fullscreenExit: [
    'M4 8v-2c0 -.551 .223 -1.05 .584 -1.412',
    'M4 16v2a2 2 0 0 0 2 2h2',
    'M16 4h2a2 2 0 0 1 2 2v2',
    'M16 20h2c.545 0 1.04 -.218 1.4 -.572',
    'M3 3l18 18',
  ],
  group: [
    'M3 5a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M17 5a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M3 19a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M17 19a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M5 7v10',
    'M19 7v10',
    'M7 5h10',
    'M7 19h10',
    'M8 8H12.8V11.2H16V16H11.2V12.8H8V8Z',
  ],
  hand: [
    'M8 13v-7.5a1.5 1.5 0 0 1 3 0v6.5',
    'M11 5.5v-2a1.5 1.5 0 1 1 3 0v8.5',
    'M14 5.5a1.5 1.5 0 0 1 3 0v6.5',
    'M17 7.5a1.5 1.5 0 0 1 3 0v8.5a6 6 0 0 1 -6 6h-2h.208a6 6 0 0 1 -5.012 -2.7a69.74 69.74 0 0 1 -.196 -.3c-.312 -.479 -1.407 -2.388 -3.286 -5.728a1.5 1.5 0 0 1 .536 -2.022a1.867 1.867 0 0 1 2.28 .28l1.47 1.47',
  ],
  highlight: [
    // the v2 marker block behind the "A" — visible only when accented
    {
      d: 'M4 6h16a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2H4a2 2 0 0 1 -2 -2V8a2 2 0 0 1 2 -2z',
      fill: 'primary',
    },
    'M8 16v-8a4 4 0 1 1 8 0v8',
    'M8 10h8',
  ],
  horizontal: ['M7 8l-4 4l4 4', 'M17 8l4 4l-4 4', 'M3 12l18 0'],
  inkHighlighter: [
    'M3 19h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4',
    'M12.5 5.5l4 4',
    'M4.5 13.5l4 4',
    { d: 'M21 15v4h-8l4 -4l4 0', stroke: 'primary', fill: 'primary' },
  ],
  insertText: [
    { d: 'M3 7v-2h12v2', stroke: 'primary' },
    { d: 'M9 5v14', stroke: 'primary' },
    { d: 'M7 19h4', stroke: 'primary' },
    { d: 'M18 15l2 2h-5', stroke: 'primary' },
    { d: 'M18 19l2 -2', stroke: 'primary' },
  ],
  italic: ['M11 5l6 0', 'M7 19l6 0', 'M14 5l-4 14'],
  line: [{ d: 'M20 4l-16 16', stroke: 'primary' }],
  // Both strokes tag the `primary` accent slot so the arrow glyph previews the
  // tool's stroke colour (like the plain `line` icon); with no accent they render
  // as currentColor — identical to the untagged glyph.
  lineArrow: [
    { d: 'M20 4l-16 16', stroke: 'primary' },
    { d: 'M16 3h5v5', stroke: 'primary' },
  ],
  link: [
    'M9 15l6 -6',
    'M11 6l.463 -.536a5 5 0 0 1 7.071 7.072l-.534 .464',
    'M13 18l-.397 .534a5.068 5.068 0 0 1 -7.127 0a4.972 4.972 0 0 1 0 -7.071l.524 -.463',
  ],
  linkOff: [
    'M9 15l3 -3m2 -2l1 -1',
    'M11 6l.463 -.536a5 5 0 0 1 7.072 7.071l-.534 .464',
    'M13 18l-.397 .534a5.068 5.068 0 0 1 -7.127 0a4.972 4.972 0 0 1 0 -7.071l.524 -.463',
    'M3 3l18 18',
  ],
  listTree: ['M9 6h11', 'M12 12h8', 'M15 18h5', 'M5 6v.01', 'M8 12v.01', 'M11 18v.01'],
  lock: [
    'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1 -2 2H5a2 2 0 0 1 -2 -2v-7a2 2 0 0 1 2 -2z',
    'M7 11V7a5 5 0 0 1 10 0v4',
  ],
  marqueeSelect: [
    'M17 20h6',
    'M20 17v6',
    'M4 13v-2',
    'M4 6v-1a1 1 0 0 1 1 -1h1',
    'M6 20h-1a1 1 0 0 1 -1 -1v-1',
    'M11 4h2',
    'M11 20h2',
    'M18 4h1a1 1 0 0 1 1 1v1',
    'M20 11v2',
  ],
  menu: ['M4 8l16 0', 'M4 16l16 0'],
  message: [
    'M8 9h8',
    'M8 13h6',
    'M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12',
  ],
  palette: [
    'M12 21a9 9 0 0 1 0 -18c4.97 0 9 3.582 9 8c0 1.06 -.474 2.078 -1.318 2.828c-.844 .75 -1.989 1.172 -3.182 1.172h-2.5a2 2 0 0 0 -1 3.75a1.3 1.3 0 0 1 -1 2.25',
    'M8.5 10.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
    'M12.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
    'M16.5 10.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
  ],
  paperclip: [
    'M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5',
  ],
  pencilMarker: [
    'm9.109 16.275 8.856-8.097c.812-.743.87-2.014.127-2.826s-2.014-.869-2.826-.127L6.41 13.322l-.127 2.826zM13.79 6.575l2.7 2.952',
    {
      d: 'M19.375 20.125c.569.063-4.05-.562-6.412-.437s-4.759 1.229-6.857 1.625c-1.764.687-3.404-.938-1.981-2.5',
      stroke: 'primary',
    },
  ],
  photo: [
    'M15 8h.01',
    'M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3v-12z',
    'M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5',
    'M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3',
  ],
  // hand-added: the v2 file draws <line> primitives the extractor skips
  plus: ['M12 5l0 14', 'M5 12l14 0'],
  pointer: [
    'M7.904 17.563a1.2 1.2 0 0 0 2.228 .308l2.09 -3.093l4.907 4.907a1.067 1.067 0 0 0 1.509 0l1.047 -1.047a1.067 1.067 0 0 0 0 -1.509l-4.907 -4.907l3.113 -2.09a1.2 1.2 0 0 0 -.309 -2.228l-13.582 -3.904l3.904 13.563z',
  ],
  polygon: [
    {
      d: 'M13.163 2.168l8.021 5.828c.694 .504 .984 1.397 .719 2.212l-3.064 9.43a1.978 1.978 0 0 1 -1.881 1.367h-9.916a1.978 1.978 0 0 1 -1.881 -1.367l-3.064 -9.43a1.978 1.978 0 0 1 .719 -2.212l8.021 -5.828a1.978 1.978 0 0 1 2.326 0z',
      stroke: 'primary',
      fill: 'secondary',
    },
  ],
  print: [
    'M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2',
    'M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4',
    'M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z',
  ],
  redact: [
    'M7 4h10',
    'M12 4v8',
    'M-7 24l12 -12',
    'M-3 24l12 -12',
    'M1 24l12 -12',
    'M5 24l12 -12',
    'M9 24l12 -12',
    'M13 24l12 -12',
    'M17 24l12 -12',
    'M21 24l12 -12',
    'M25 24l12 -12',
  ],
  // v2 parity: dashed frame + a SMALL rounded hatched box (v2 clips the
  // hatch via <clipPath>; this icon system is plain paths, so the hatch
  // segments are pre-clipped to the 10..22 box analytically).
  redactArea: [
    'M6 20h-1a2 2 0 0 1 -2 -2v-1',
    'M3 13v-3',
    'M3 6v-1a2 2 0 0 1 2 -2h1',
    'M10 3h3',
    'M17 3h1a2 2 0 0 1 2 2v1',
    'M12 10h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2v-8a2 2 0 0 1 2 -2z',
    'M10 12l2 -2',
    'M10 16l6 -6',
    'M10 20l10 -10',
    'M12 22l10 -10',
    'M16 22l6 -6',
    'M20 22l2 -2',
  ],
  redactCombined: [
    'M6 20h-1a2 2 0 0 1 -2 -2v-1',
    'M3 13v-3',
    'M20 13v-3',
    'M3 6v-1a2 2 0 0 1 2 -2h1',
    'M10 3h3',
    'M17 3h1a2 2 0 0 1 2 2v1',
    'M8 8h8',
    'M12 8v7',
    'M6 19l12 -12',
    'M6 23l12 -12',
    'M10 23l12 -12',
    'M14 27l12 -12',
    'M14 23l12 -12',
  ],
  redactionSidebar: [
    'M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z',
    'M15 4v16',
    'M7 8h5',
    'M7 11h5',
    'M7 14h4',
  ],
  replaceText: [
    { d: 'M3 7v-2h12v2', stroke: 'primary' },
    { d: 'M9 5v14', stroke: 'primary' },
    { d: 'M7 19h4', stroke: 'primary' },
    { d: 'M20 14l2 2h-3', stroke: 'primary' },
    { d: 'M20 18l2 -2', stroke: 'primary' },
    { d: 'M19 16a3 3 0 1 0 2 5.236', stroke: 'primary' },
  ],
  rotateClockwise: ['M4.05 11a8 8 0 1 1 .5 4m-.5 5v-5h5'],
  rotateCounterClockwise: ['M19.95 11a8 8 0 1 0 -.5 4m.5 5v-5h-5'],
  rubberStamp: [
    'M21 17.85h-18c0 -4.05 1.421 -4.05 3.79 -4.05c5.21 0 1.21 -4.59 1.21 -6.8a4 4 0 1 1 8 0c0 2.21 -4 6.8 1.21 6.8c2.369 0 3.79 0 3.79 4.05',
    'M5 21h14',
  ],
  rubberStampPlus: [
    'M21 17.85h-18c0 -4.05 1.421 -4.05 3.79 -4.05c5.21 0 1.21 -4.59 1.21 -6.8a4 4 0 0 1 4.9 -4.1',
    'M16 10c0 1.2 -4 3.8 1.21 3.8c2.369 0 3.79 0 3.79 4.05',
    'M5 21h14',
    'M16 5h6',
    'M19 2v6',
  ],
  save: [
    'M6 4h10l4 4v10a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2',
    'M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M14 4l0 4l-6 0l0 -4',
  ],
  screenshot: [
    'M7 19a2 2 0 0 1 -2 -2',
    'M5 13v-2',
    'M5 7a2 2 0 0 1 2 -2',
    'M11 5h2',
    'M17 5a2 2 0 0 1 2 2',
    'M19 11v2',
    'M19 17v4',
    'M21 19h-4',
    'M13 19h-2',
  ],
  search: ['M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0', 'M21 21l-6 -6'],
  settings: [
    'M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z',
    'M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0',
  ],
  sidebar: [
    'M4 18v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z',
    'M10 18v-12a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z',
  ],
  signature: [
    'M3 17c3.333 -3.333 5 -6 5 -8c0 -3 -1 -3 -2 -3s-2.032 1.085 -2 3c.034 2.048 1.658 4.877 2.5 6c1.5 2 2.5 2.5 3.5 1l2 -3c.333 2.667 1.333 4 3 4c.53 0 2.639 -2 3 -2c.517 0 1.517 .667 3 2',
  ],
  singlePage: ['M5 3m0 1a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1z'],
  square: [
    {
      d: 'M3 3m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z',
      stroke: 'primary',
      fill: 'secondary',
    },
  ],
  squares: [
    'M8 10a2 2 0 0 1 2 -2h9a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-9a2 2 0 0 1 -2 -2z',
    'M16 8v-3a2 2 0 0 0 -2 -2h-9a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h3',
  ],
  squiggly: [
    'M8 16v-8a4 4 0 1 1 8 0v8',
    'M8 10h8',
    { d: 'M4 20c1.5 -1.5 3.5 -1.5 5 0s3.5 1.5 5 0 3.5 -1.5 5 0', stroke: 'primary' },
  ],
  strikethrough: ['M8 16v-8a4 4 0 1 1 8 0v8', { d: 'M4 10h16', stroke: 'primary' }],
  text: [
    'M6.5 15.5m-3.5 0a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0',
    'M14 19v-10.5a3.5 3.5 0 0 1 7 0v10.5',
    'M14 13h7',
    'M10 12v7',
  ],
  trash: [
    'M4 7l16 0',
    'M10 11l0 6',
    'M14 11l0 6',
    'M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12',
    'M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3',
  ],
  underline: [{ d: 'M4 20h16', stroke: 'primary' }, 'M8 16v-8a4 4 0 1 1 8 0v8', 'M8 10h8'],
  // the v2 ungroup glyph: two separated rounded rects
  ungroup: [
    'M4 5a1 1 0 0 1 1 -1h5a1 1 0 0 1 1 1v5a1 1 0 0 1 -1 1h-5a1 1 0 0 1 -1 -1z',
    'M13 14a1 1 0 0 1 1 -1h5a1 1 0 0 1 1 1v5a1 1 0 0 1 -1 1h-5a1 1 0 0 1 -1 -1z',
  ],
  unlock: ['M7 11V7a5 5 0 0 1 9.9-1'],
  vertical: ['M8 7l4 -4l4 4', 'M8 17l4 4l4 -4', 'M12 3l0 18'],
  viewSettings: [
    'M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M12 10.5v1.5',
    'M12 16v1.5',
    'M15.031 12.25l-1.299 .75',
    'M10.268 15l-1.3 .75',
    'M15 15.803l-1.285 -.773',
    'M10.285 12.97l-1.285 -.773',
    'M14 3v4a1 1 0 0 0 1 1h4',
    'M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z',
  ],
  widgetEdit: [
    'M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1',
    'M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415',
    'M16 5l3 3',
  ],
  x: ['M18 6l-12 12', 'M6 6l12 12'],
  zigzag: [{ d: 'M12 2.4L21.36 11.76L2.64 12.24L12 21.6', stroke: 'primary' }],
  zoomIn: ['M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0', 'M9 12h6', 'M12 9v6'],
  zoomInArea: [
    'M15 13v4',
    'M13 15h4',
    'M15 15m-5 0a5 5 0 1 0 10 0a5 5 0 1 0 -10 0',
    'M22 22l-3 -3',
    'M6 18h-1a2 2 0 0 1 -2 -2v-1',
    'M3 11v-1',
    'M3 6v-1a2 2 0 0 1 2 -2h1',
    'M10 3h1',
    'M15 3h1a2 2 0 0 1 2 2v1',
  ],
  zoomOut: ['M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0', 'M9 12l6 0'],
};

export type IconName = keyof typeof ICON_PATHS;

export interface IconProps {
  name: string;
  size?: number;
  strokeWidth?: number;
  className?: string;
  title?: string;
  /** Paints the glyph's slot-tagged sub-paths (a tool previewing its
   *  drawing defaults). Absent → the plain, untinted glyph. */
  accent?: IconAccent;
}

/** One SVG renderer for every icon. Unknown names render nothing (caller shows
 *  a text fallback), so a missing glyph never throws. */
export function Icon({ name, size = 20, strokeWidth = 2, className, title, accent }: IconProps) {
  // Registry semantics: user icons are additive and a colliding name WINS —
  // re-skinning a built-in glyph is a supported move, not an accident.
  const custom = useCustomIcons();
  const paths = custom[name] ?? ICON_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role="img"
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {paths.map((p, i) => {
        if (typeof p === 'string') return <path key={i} d={p} />;
        const fill = p.fill === true ? 'currentColor' : p.fill ? accent?.[p.fill] : undefined;
        // a stroke slot paints from the accent, inheriting currentColor
        // without one; a fill-ONLY path never gets an outline (the v2 look)
        const stroke = p.stroke ? accent?.[p.stroke] : p.fill ? 'none' : undefined;
        return <path key={i} d={p.d} fill={fill ?? 'none'} stroke={stroke} />;
      })}
    </svg>
  );
}

export const hasIcon = (name: string): boolean => name in ICON_PATHS;
